/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

/* global document, Office */

import {
  bootstrapSessionFromStorage,
  logout,
  sendMagicLink,
  signInWithPassword,
} from "../core/auth-service";
import { buildTimeFinderPayload } from "../core/request-builder";
import { getOutlookContext } from "../core/outlook-context";
import { TimeFinderService } from "../core/timefinder-service";
import { formatTimeWindow, timeWindowToDates, msToSlot, slotToMs } from "../core/time-window";
import { classifySegmentDifficulty } from "../core/timeline-utils";
import { ApiClient } from "../core/api-client";
import { MOCK_CALENDAR_LINK, MOCK_CONTEXT, MOCK_SESSION, MOCK_STATUS_ROW } from "./mock-data";
import type {
  HeyeaSession,
  OutlookContext,
  NormalizedAttendee,
  AttendeeWithResolution,
  UIState,
  CreateEventRequest,
} from "../core/types";
import type { TimeFinderRequestStatusDto } from "@hai/common";
import type { TimeFinderResult } from "@hai/time-finder";
import { findIana } from "windows-iana";

type ViewState = "loading" | "unauthenticated" | "authenticated" | "error";

class TaskpaneApp {
  private readonly mockMode = this.detectMockMode();
  private session: HeyeaSession | null = null;
  private context: OutlookContext | null = null;
  private readonly timeFinder = new TimeFinderService();
  private attendeesWithResolution: AttendeeWithResolution[] = [];
  private customAttendees: Map<string, NormalizedAttendee> = new Map();
  private removedAttendees: Set<string> = new Set();
  private currentResult: TimeFinderResult | null = null;
  private uiState: UIState = {
    titleDescriptionExpanded: false,
    preferencesExpanded: false,
    debugExpanded: false,
    timelineExpanded: false,
    sessionExpanded: false,
    attendeesExpanded: true,
    recommendationExpanded: true,
  };

  // DOM elements - initialized after DOM ready
  private shell!: HTMLElement;
  private views!: Record<ViewState | "error", HTMLElement>;

  private loginForm!: HTMLFormElement;
  private loginError!: HTMLElement;
  private magicLinkStatus!: HTMLElement | null;
  private sessionEmail!: HTMLElement;
  private attendeeError!: HTMLElement | null;
  private attendeeList!: HTMLUListElement;
  private recommendButton!: HTMLButtonElement;
  private refreshButton!: HTMLButtonElement;
  private logoutButton!: HTMLButtonElement;
  private resultsStatus!: HTMLElement;
  private resultsJson!: HTMLElement;
  private errorMessage!: HTMLElement;
  private errorRetry!: HTMLButtonElement;
  private otpButton!: HTMLButtonElement | null;
  private recommendationSummary!: HTMLElement;
  private bestTimeText!: HTMLElement;
  private bookMeetingBtn!: HTMLButtonElement;
  private bookingStatus!: HTMLElement;
  private calendarLink!: HTMLAnchorElement;
  private collapsibleSections!: HTMLElement;
  private eventTitle!: HTMLInputElement;
  private eventDescription!: HTMLTextAreaElement;
  private timelineSection!: HTMLElement;
  private timelineGrid!: HTMLElement;
  private timelineDate!: HTMLElement;

  private currentTimelineDay = 0; // Day offset from today
  private currentTimelineScale = 60; // minutes per cell
  private currentAttendeeView: string | null | "combined" = null; // null = all, email = specific, "combined" = merged view
  private userSelectedTimeRange: { start: number; end: number } | null = null;
  private isSelecting = false;
  private selectionStartSlot: number | null = null;
  private timelineLayers: unknown[] = [];
  private lastDiagnostics: unknown = null;

  private async waitForDomReady(): Promise<void> {
    if (document.readyState === "complete" || document.readyState === "interactive") {
      return;
    }

    await new Promise<void>((resolve) => {
      document.addEventListener("DOMContentLoaded", () => resolve(), { once: true });
    });
  }

  private initializeElements(): void {
    console.log("[Taskpane] DOM is ready, initializing elements");

    // Core shell and views
    this.shell = document.getElementById("app-shell")!;
    this.views = {
      loading: document.getElementById("state-loading") as HTMLElement,
      unauthenticated: document.getElementById("state-unauthenticated") as HTMLElement,
      authenticated: document.getElementById("state-authenticated") as HTMLElement,
      error: document.getElementById("state-error") as HTMLElement,
    } as Record<ViewState | "error", HTMLElement>;

    // Authentication elements
    this.loginForm = document.getElementById("login-form") as HTMLFormElement;
    this.loginError = document.getElementById("login-error") as HTMLElement;
    this.magicLinkStatus = document.getElementById("magic-link-status") as HTMLElement | null;
    this.sessionEmail = document.getElementById("session-email") as HTMLElement;

    // Attendee management elements
    this.attendeeError = document.getElementById("attendee-error") as HTMLElement | null;
    this.attendeeList = document.getElementById("attendee-list") as HTMLUListElement;

    // Action buttons
    this.recommendButton = document.getElementById("recommend-button") as HTMLButtonElement;
    this.refreshButton = document.getElementById("refresh-context") as HTMLButtonElement;
    this.logoutButton = document.getElementById("logout-button") as HTMLButtonElement;
    this.otpButton = document.getElementById("send-magic-link") as HTMLButtonElement | null;

    // Results and status elements
    this.resultsStatus = document.getElementById("results-status") as HTMLElement;
    this.resultsJson = document.getElementById("results-json") as HTMLElement;

    // Error handling elements
    this.errorMessage = document.getElementById("error-message") as HTMLElement;
    this.errorRetry = document.getElementById("error-retry") as HTMLButtonElement;

    // Recommendation and booking elements
    this.recommendationSummary = document.getElementById("recommendation-summary") as HTMLElement;
    this.bestTimeText = document.getElementById("best-time-text") as HTMLElement;
    this.bookMeetingBtn = document.getElementById("book-meeting-btn") as HTMLButtonElement;
    this.bookingStatus = document.getElementById("booking-status") as HTMLElement;
    this.calendarLink = document.getElementById("calendar-link") as HTMLAnchorElement;

    // Collapsible sections
    this.collapsibleSections = document.getElementById("collapsible-sections") as HTMLElement;

    // Event details elements
    this.eventTitle = document.getElementById("event-title") as HTMLInputElement;
    this.eventDescription = document.getElementById("event-description") as HTMLTextAreaElement;

    // Timeline elements
    this.timelineSection = document.getElementById("timeline-section") as HTMLElement;
    this.timelineGrid = document.getElementById("timeline-grid") as HTMLElement;
    this.timelineDate = document.getElementById("timeline-date") as HTMLElement;

    // Validate critical elements are present
    if (!this.shell) {
      console.error("[Taskpane] Critical element 'app-shell' not found");
      throw new Error("[Taskpane] Critical element 'app-shell' not found");
    }
    if (!this.attendeeList) {
      console.error("[Taskpane] Critical element 'attendee-list' not found");
      throw new Error("[Taskpane] Critical element 'attendee-list' not found");
    }
    if (!this.recommendButton) {
      console.error("[Taskpane] Critical element 'recommend-button' not found");
      throw new Error("[Taskpane] Critical element 'recommend-button' not found");
    }

    console.log("[Taskpane] All DOM elements initialized successfully");
  }

  private detectMockMode(): boolean {
    const hasWindowFlag =
      typeof window !== "undefined" && window.location.search.includes("mock=1");
    const hasEnvFlag =
      typeof process !== "undefined" && Boolean(process.env?.HAI_OUTLOOK_MOCK === "1");
    return hasWindowFlag || hasEnvFlag;
  }

  async init(): Promise<void> {
    await this.waitForDomReady();
    this.initializeElements();

    if (this.mockMode) {
      console.warn("[Taskpane] Mock mode enabled");
    }
    this.shell.classList.remove("hidden");
    this.bindEvents();
    this.initializeSections();
    await this.restoreSession();
  }

  private bindEvents(): void {
    try {
      console.log("[Taskpane] Binding event handlers");

      // Authentication events
      this.loginForm.addEventListener("submit", (event) => {
        event.preventDefault();
        void this.handleLogin();
      });

      if (this.otpButton) {
        this.otpButton.addEventListener("click", () => void this.handleMagicLink());
      }

      this.logoutButton.addEventListener("click", () => void this.performLogout());

      // Attendee and recommendation events
      this.recommendButton.addEventListener("click", () => void this.handleRecommendations());
      this.refreshButton.addEventListener("click", () => void this.refreshContext());

      // Booking events
      this.bookMeetingBtn.addEventListener("click", () => {
        console.log("[Taskpane] Book meeting button clicked");
        void this.handleBookMeeting();
      });

      // Collapsible section toggles
      document
        .getElementById("title-desc-toggle")
        ?.addEventListener("click", () =>
          this.toggleSection("titleDescriptionExpanded", "title-desc-content")
        );

      document
        .getElementById("preferences-toggle")
        ?.addEventListener("click", () =>
          this.toggleSection("preferencesExpanded", "preferences-content")
        );

      document
        .getElementById("debug-toggle")
        ?.addEventListener("click", () => this.toggleSection("debugExpanded", "debug-content"));

      document
        .getElementById("timeline-toggle")
        ?.addEventListener("click", () =>
          this.toggleSection("timelineExpanded", "timeline-content")
        );

      document
        .getElementById("session-toggle")
        ?.addEventListener("click", () =>
          this.toggleSection("sessionExpanded", "session-content")
        );

      document
        .getElementById("attendees-toggle")
        ?.addEventListener("click", () =>
          this.toggleSection("attendeesExpanded", "attendees-content")
        );

      document
        .getElementById("recommendation-toggle")
        ?.addEventListener("click", () =>
          this.toggleSection("recommendationExpanded", "recommendation-content")
        );

      // Timeline navigation
      document
        .getElementById("timeline-prev-day")
        ?.addEventListener("click", () => this.changeTimelineDay(-1));

      document
        .getElementById("timeline-next-day")
        ?.addEventListener("click", () => this.changeTimelineDay(1));

      // Timeline scale control
      const scaleSelect = document.getElementById("timeline-scale-select") as HTMLSelectElement;
      if (scaleSelect) {
        scaleSelect.value = "60";
        scaleSelect.addEventListener("change", () => {
          this.handleTimelineScaleChange(parseInt(scaleSelect.value, 10));
        });
      }

      // Attendee view control
      const attendeeViewSelect = document.getElementById("attendee-view-select") as HTMLSelectElement;
      if (attendeeViewSelect) {
        attendeeViewSelect.addEventListener("change", () => {
          this.handleAttendeeViewChange(attendeeViewSelect.value);
        });
      }

      // Clear time selection button
      const clearTimeSelectionBtn = document.getElementById("clear-time-selection");
      if (clearTimeSelectionBtn) {
        clearTimeSelectionBtn.addEventListener("click", () => this.clearTimeSelection());
      }

      // Error retry
      this.errorRetry.addEventListener("click", () => void this.restoreSession());

      // Global mouseup for timeline selection
      document.addEventListener("mouseup", () => this.handleTimelineMouseUp());

      console.log("[Taskpane] Event handlers bound successfully");
    } catch (error) {
      console.error("[Taskpane] Failed to bind event handlers:", error);
      throw error; // Re-throw to prevent app from running with unbound events
    }
  }

  private initializeSections(): void {
    this.applySectionState("titleDescriptionExpanded", "title-desc-content");
    this.applySectionState("preferencesExpanded", "preferences-content");
    this.applySectionState("debugExpanded", "debug-content");
    this.applySectionState("timelineExpanded", "timeline-content");
    this.applySectionState("sessionExpanded", "session-content");
    this.applySectionState("attendeesExpanded", "attendees-content");
    this.applySectionState("recommendationExpanded", "recommendation-content");
  }

  private setView(view: ViewState): void {
    (Object.keys(this.views) as Array<ViewState | "error">).forEach((key) => {
      if (this.views[key]) {
        this.views[key].classList.toggle("hidden", key !== view);
      }
    });
  }

  private async restoreSession(): Promise<void> {
    this.setView("loading");
    try {
      if (this.mockMode) {
        this.session = { ...MOCK_SESSION };
        this.sessionEmail.textContent = this.session.user?.email ?? "Unknown user";
        this.setView("authenticated");
        await this.refreshContext();
        return;
      }
      this.session = await bootstrapSessionFromStorage();
      if (this.session) {
        this.sessionEmail.textContent = this.session.user?.email ?? "Unknown user";
        this.setView("authenticated");
        await this.refreshContext();
      } else {
        // Session restoration failed - show unauthenticated state
        this.resetLoginState();
        this.setView("unauthenticated");
        // Show a friendly message explaining why re-login is needed
        if (this.loginError) {
          this.loginError.textContent = "Session expired. Please sign in again.";
        }
      }
    } catch (err) {
      // Unexpected error during restoration - clear state and show error
      this.resetLoginState();
      this.setView("unauthenticated");
      if (this.loginError) {
        this.loginError.textContent =
          "Unable to restore session. Please sign in again.";
      }
      console.error("[TaskpaneApp] Session restoration error:", err);
    }
  }

  private async handleLogin(): Promise<void> {
    const emailInput = document.getElementById("login-email") as HTMLInputElement;
    const passwordInput = document.getElementById("login-password") as HTMLInputElement;
    this.loginError.textContent = "";
    if (this.magicLinkStatus) {
      this.magicLinkStatus.textContent = "";
      this.magicLinkStatus.classList.add("hidden");
    }
    const loginControls = this.loginForm.querySelectorAll("button, input");
    loginControls.forEach((el) => ((el as HTMLButtonElement | HTMLInputElement).disabled = true));
    try {
      this.session = await signInWithPassword(emailInput.value, passwordInput.value);
      this.sessionEmail.textContent = this.session.user?.email ?? "Unknown user";
      this.setView("authenticated");
      await this.refreshContext();
    } catch (err) {
      this.loginError.textContent = (err as Error).message ?? "Login failed";
      this.setView("unauthenticated");
    } finally {
      loginControls.forEach(
        (el) => ((el as HTMLButtonElement | HTMLInputElement).disabled = false)
      );
    }
  }

  private async handleMagicLink(): Promise<void> {
    const emailInput = document.getElementById("login-email") as HTMLInputElement;
    this.loginError.textContent = "";
    if (this.magicLinkStatus) {
      this.magicLinkStatus.textContent = "";
      this.magicLinkStatus.classList.add("hidden");
    }
    emailInput.setCustomValidity("");
    const email = emailInput.value.trim();
    if (!email) {
      emailInput.setCustomValidity("Email is required");
      emailInput.reportValidity();
      emailInput.focus();
      return;
    }

    emailInput.disabled = true;
    if (this.otpButton) {
      this.otpButton.disabled = true;
    }
    try {
      await sendMagicLink(email);
      if (this.magicLinkStatus) {
        this.magicLinkStatus.textContent = "Magic link sent. Check your email to continue.";
        this.magicLinkStatus.classList.remove("hidden");
      }
    } catch (err) {
      this.loginError.textContent = "Unable to send magic link. Please try again.";
      console.error("[Taskpane] magic link error", err);
    } finally {
      emailInput.disabled = false;
      emailInput.setCustomValidity("");
      if (this.otpButton) {
        this.otpButton.disabled = false;
      }
    }
  }

  private async refreshContext(): Promise<void> {
    try {
      if (this.mockMode) {
        this.context = { ...MOCK_CONTEXT, attendees: [...MOCK_CONTEXT.attendees] };
        this.removedAttendees.clear();
        this.renderAttendees();
        return;
      }
      this.context = await getOutlookContext();
      this.removedAttendees.clear();
      this.renderAttendees();
    } catch (err) {
      console.error("[Taskpane] Failed to read Outlook context", err);
      this.showError(err as Error);
    }
  }

  private renderAttendees(): void {
    try {
      if (!this.context) {
        this.attendeeList.innerHTML = "<li>No attendees detected.</li>";
        return;
      }

      // Merge context attendees with custom additions, filtering out removed ones
      const allAttendees = [...this.context.attendees].filter(
        (a) => !this.removedAttendees.has(a.email)
      );
      this.customAttendees.forEach((attendee) => {
        if (!this.removedAttendees.has(attendee.email) && !allAttendees.find((a) => a.email === attendee.email)) {
          allAttendees.push(attendee);
        }
      });

      console.log(`[Taskpane] Rendering ${allAttendees.length} attendees`);
      this.attendeeList.innerHTML = "";

      allAttendees.forEach((attendee) => {
        const li = document.createElement("li");
        li.className = "attendee-item";

        // Find resolution info if available
        const withResolution = this.attendeesWithResolution.find((a) => a.email === attendee.email);

        const nameSpan = document.createElement("span");
        nameSpan.className = "attendee-name";
        nameSpan.textContent = `${attendee.displayName} (${attendee.role})`;

        const emailSpan = document.createElement("span");
        emailSpan.className = "attendee-email";
        emailSpan.textContent = attendee.email;

        li.appendChild(nameSpan);
        li.appendChild(emailSpan);

        // Add resolution badge if available
        if (withResolution?.resolvedVia) {
          const badge = document.createElement("span");
          badge.className = `resolution-badge resolution-${withResolution.resolvedVia}`;
          badge.textContent = this.formatResolutionStatus(withResolution.resolvedVia);
          li.appendChild(badge);
        }

        // Add remove button for non-self attendees
        if (attendee.role !== "self") {
          const removeBtn = document.createElement("button");
          removeBtn.className = "ghost icon-btn";
          removeBtn.innerHTML = "×";
          removeBtn.title = "Remove attendee";
          removeBtn.onclick = () => {
            console.log(`[Taskpane] Remove attendee clicked for: ${attendee.email}`);
            this.removeAttendee(attendee.email);
          };
          li.appendChild(removeBtn);
        }

        this.attendeeList.appendChild(li);
      });

      // Add input for new attendee
      const addLi = document.createElement("li");
      addLi.className = "attendee-add";

      const input = document.createElement("input");
      input.type = "email";
      input.placeholder = "Add attendee by email...";
      input.id = "new-attendee-email";
      input.className = "attendee-input";

      const addBtn = document.createElement("button");
      addBtn.textContent = "Add";
      addBtn.className = "ghost";
      addBtn.onclick = () => {
        console.log("[Taskpane] Add attendee button clicked");
        this.addAttendee();
      };

      addLi.appendChild(input);
      addLi.appendChild(addBtn);
      this.attendeeList.appendChild(addLi);
    } catch (error) {
      console.error("[Taskpane] Failed to render attendees:", error);
      if (this.attendeeError) {
        this.attendeeError.textContent = "Failed to display attendees. Please refresh.";
        this.attendeeError.classList.remove("hidden");
      }
    }
  }

  private formatResolutionStatus(status: string): string {
    const labels: Record<string, string> = {
      provider_connection: "✓ Connected",
      user_profile: "✓ Profile",
      external_calendar: "✓ External",
      none: "✗ Not found",
    };
    return labels[status] || status;
  }

  private addAttendee(): void {
    const input = document.getElementById("new-attendee-email") as HTMLInputElement;
    const email = input.value.trim();

    this.showAttendeeError("");

    if (!email) return;

    // Basic email validation
    if (!email.includes("@")) {
      this.showAttendeeError("Please enter a valid email address.");
      return;
    }

    // Check if already exists
    const allAttendees = [...(this.context?.attendees || [])];
    this.customAttendees.forEach((a) => allAttendees.push(a));

    if (allAttendees.find((a) => a.email.toLowerCase() === email.toLowerCase())) {
      this.showAttendeeError("Attendee already in the list.");
      return;
    }

    // Add to custom attendees
    this.customAttendees.set(email, {
      email,
      displayName: email.split("@")[0],
      role: "optional",
    });

    input.value = "";
    this.renderAttendees();
  }

  private removeAttendee(email: string): void {
    this.customAttendees.delete(email);
    this.removedAttendees.add(email);
    this.renderAttendees();
  }

  private getAllAttendees(): NormalizedAttendee[] {
    const allAttendees = [...(this.context?.attendees || [])].filter(
      (a) => !this.removedAttendees.has(a.email)
    );
    this.customAttendees.forEach((attendee) => {
      if (!allAttendees.find((a) => a.email === attendee.email)) {
        allAttendees.push(attendee);
      }
    });
    // Always include the logged-in Heyea session email as a self attendee
    if (this.session?.user?.email) {
      const email = this.session.user.email.trim();
      const exists = allAttendees.find((a) => a.email.toLowerCase() === email.toLowerCase());
      if (!exists) {
        allAttendees.push({
          email: email.toLowerCase(),
          displayName: email.split("@")[0] || email,
          role: "self",
        });
      }
    }
    return allAttendees;
  }

  private async handleRecommendations(): Promise<void> {
    if (!this.session) {
      this.setView("unauthenticated");
      return;
    }
    if (!this.context) {
      await this.refreshContext();
    }
    if (!this.context) {
      this.resultsStatus.textContent = "Unable to read Outlook context.";
      return;
    }

    this.recommendButton.disabled = true;
    this.resultsStatus.textContent = "Requesting recommendations from Heyea, hang tight.";
    this.resultsJson.textContent = "";
    if (this.mockMode) {
      const mockRow = { ...MOCK_STATUS_ROW, updatedAt: new Date().toISOString() };
      this.renderResult(mockRow);
      this.recommendButton.disabled = false;
      return;
    }
    const payload = buildTimeFinderPayload({
      session: this.session,
      context: this.context,
      attendees: this.getAllAttendees(),
    });
    try {
      const finalRow = await this.timeFinder.submitAndWatch(payload, (row) =>
        this.renderResult(row)
      );
      this.renderResult(finalRow);
    } catch (err) {
      this.resultsStatus.textContent = "Request failed. Please try again.";
      console.error("[Taskpane] recommendation error", err);
    } finally {
      this.recommendButton.disabled = false;
    }
  }

  private renderResult(statusRow: TimeFinderRequestStatusDto | null): void {
    try {
      if (!statusRow) {
        return;
      }

      console.log(`[Taskpane] Rendering result with status: ${statusRow.status ?? "UNKNOWN"}`);

      // Reset user selection when new results arrive
      this.userSelectedTimeRange = null;

      const { result, diagnostics } = this.normalizeResultPayload(statusRow);
      this.currentResult = result;
      this.lastDiagnostics = diagnostics;

      this.resultsStatus.textContent = `Status: ${statusRow.status ?? "UNKNOWN"}`;
      this.resultsJson.textContent = JSON.stringify(
        { status: statusRow.status, result, diagnostics },
        null,
        2
      );

      // Reset booking artifacts for a fresh recommendation
      this.bookingStatus.textContent = "";
      this.calendarLink.classList.add("hidden");
      this.calendarLink.removeAttribute("href");

      // Panels stay visible; default placeholders until data arrives
      this.recommendationSummary.classList.remove("hidden");
      this.collapsibleSections.classList.remove("hidden");
      this.timelineSection.classList.remove("hidden");
      this.bookMeetingBtn.disabled = true;

      const attendeeLayers = this.pickAttendeeLayers(result, diagnostics);

      if (statusRow.status === "DONE" && result?.rec) {
        // Set recommendation time only when DONE
        this.bestTimeText.textContent = `${result.rec.startIso ?? "UNKNOWN"}`;
        // Show summary and booking UI
        this.bookMeetingBtn.disabled = false;

        // Format and display the best time recommendation
        const timezone = this.context?.timezone || "UTC";
        const timeWindow = formatTimeWindow(result.rec, timezone);
        this.bestTimeText.textContent = timeWindow;
        console.log(`[Taskpane] Best time recommendation: ${timeWindow}`);

        // Pre-fill title from context
        if (this.context?.subject && !this.eventTitle.value) {
          this.eventTitle.value = this.context.subject;
        }
        if (this.context?.bodyPreview && !this.eventDescription.value) {
          this.eventDescription.value = this.context.bodyPreview.slice(0, 500);
        }

        // Update attendee resolution info from result
        if (attendeeLayers.length > 0) {
          this.updateAttendeeResolution(attendeeLayers);
        }

        // Show timeline header always; expand only when we have data
        this.timelineSection.classList.remove("hidden");
        this.timelineLayers = attendeeLayers;
        this.uiState.timelineExpanded = true;
        this.applySectionState("timelineExpanded", "timeline-content");
        if (this.timelineLayers.length > 0) {
          this.currentTimelineDay = 0;
          this.populateAttendeeViewSelector();
          this.renderTimeline();
        } else {
          this.timelineGrid.textContent = "No availability timeline available.";
        }
      } else {
        // Not DONE yet - show awaiting status
        this.bestTimeText.textContent = "Awaiting recommendation";
        this.bookMeetingBtn.disabled = true;
        this.timelineLayers = [];
        this.timelineGrid.textContent = "Awaiting timeline data.";
        this.uiState.timelineExpanded = false;
        this.applySectionState("timelineExpanded", "timeline-content");
      }
    } catch (error) {
      console.error("[Taskpane] Failed to render result:", error);
      if (statusRow?.status === "FAILED") {
        this.resultsStatus.textContent = "Error displaying results. Please try again.";
      } else if (statusRow?.status === "PENDING") {
        this.resultsStatus.textContent = "Processing your request...";
      } else {
        this.resultsStatus.textContent = "An unexpected error occurred while displaying results.";
      }
    }
  }

  private updateAttendeeResolution(layers: unknown[]): void {
    this.attendeesWithResolution = [];

    layers.forEach((layer: any) => {
      if (layer.meta?.kind === "attendee") {
        const attendeeEmail = layer.meta.attendeeEmail;
        const existing = this.getAllAttendees().find((a) => a.email === attendeeEmail);

        if (existing) {
          this.attendeesWithResolution.push({
            ...existing,
            resolvedVia: layer.meta.resolvedVia,
            calendarIds: layer.meta.calendarIds || [],
          });
        }
      }
    });

    // Re-render to show resolution badges
    this.renderAttendees();
  }

  private toggleSection(stateKey: keyof UIState, contentId: string): void {
    const next = !this.uiState[stateKey];
    this.applySectionState(stateKey, contentId, next);
  }

  private async handleBookMeeting(): Promise<void> {
    if (!this.session) {
      this.bookingStatus.textContent = "Not authenticated";
      return;
    }

    const title = this.eventTitle.value.trim();
    if (!title) {
      this.bookingStatus.textContent = "Please provide a title";
      return;
    }

    const attendees = this.getAllAttendees();
    if (attendees.length === 0) {
      this.bookingStatus.textContent = "Please add at least one attendee";
      return;
    }

    let startDate: Date;
    let endDate: Date;

    if (this.userSelectedTimeRange) {
      const startMs = slotToMs(this.userSelectedTimeRange.start);
      const endMs = slotToMs(this.userSelectedTimeRange.end);
      startDate = new Date(startMs);
      endDate = new Date(endMs);
    } else if (this.currentResult?.rec) {
      try {
        [startDate, endDate] = timeWindowToDates(this.currentResult.rec);
      } catch (err) {
        this.bookingStatus.textContent = "Recommendation window is invalid.";
        return;
      }
    } else {
      this.bookingStatus.textContent = "No time selected.";
      return;
    }

    this.bookMeetingBtn.disabled = true;
    this.bookingStatus.textContent = "Creating event...";
    this.calendarLink.classList.add("hidden");

    if (this.mockMode) {
      this.bookingStatus.textContent = "Event created successfully! (mock)";
      this.calendarLink.href = MOCK_CALENDAR_LINK;
      this.calendarLink.textContent = "View in Calendar";
      this.calendarLink.classList.remove("hidden");
      this.bookMeetingBtn.disabled = false;
      return;
    }

    try {
      const timezone = this.context?.timezone || "UTC";
      const webConferencing = (document.getElementById("web-conferencing") as HTMLInputElement)
        .checked;

      const request: CreateEventRequest = {
        title,
        description: this.eventDescription.value.trim(),
        attendees: attendees.map((a) => ({ email: a.email })),
        start: startDate.toISOString(),
        end: endDate.toISOString(),
        allDay: false,
        timezone,
        meetingLink: webConferencing,
      };

      const apiClient = new ApiClient();
      const response = await apiClient.createEvent(request);

      this.bookingStatus.textContent = "Event created successfully!";
      this.calendarLink.href = response.htmlLink;
      this.calendarLink.textContent = "View in Calendar";
      this.calendarLink.classList.remove("hidden");
    } catch (err: Error | unknown) {
      console.error("[Taskpane] booking error", err);
      const msg = (err as Error)?.message || "Failed to create event";
      this.bookingStatus.textContent = `Error: ${msg}`;
    } finally {
      this.bookMeetingBtn.disabled = false;
    }
  }

  private async performLogout(): Promise<void> {
    await logout();
    this.session = null;
    this.context = null;
    this.attendeeList.innerHTML = "";
    this.resultsStatus.textContent = "Signed out.";
    this.resultsJson.textContent = "";
    this.resetLoginState();
    this.setView("unauthenticated");
  }

  private showError(error: Error): void {
    this.errorMessage.textContent = error.message ?? "Unexpected error";
    this.setView("error");
  }

  private resetLoginState(): void {
    this.loginError.textContent = "";
    if (this.magicLinkStatus) {
      this.magicLinkStatus.textContent = "";
      this.magicLinkStatus.classList.add("hidden");
    }
  }

  private changeTimelineDay(offset: number): void {
    this.currentTimelineDay += offset;
    this.renderTimeline();
  }

  private renderTimeline(): void {
    try {
      if (this.timelineLayers.length === 0) return;

      console.log(`[Taskpane] Rendering timeline for day offset: ${this.currentTimelineDay}`);

      const timezone = this.context?.timezone || "UTC";

      // Calculate the date for current timeline day
      const baseDate = new Date();
      baseDate.setHours(0, 0, 0, 0);
      baseDate.setDate(baseDate.getDate() + this.currentTimelineDay);

      this.timelineDate.textContent = baseDate.toLocaleDateString(undefined, {
        weekday: "long",
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: timezone,
      });

      // Calculate slot range for this day (assuming 24 hours)
      const dayStartMs = baseDate.getTime();
      const dayStartSlot = msToSlot(dayStartMs);

      const attendeeLayers = this.timelineLayers.filter(
        (layer: any) => layer.meta?.kind === "attendee"
      );

      let displayLayers: any[];
      if (this.currentAttendeeView === "combined") {
        const combinedLayer = this.createCombinedLayer();
        displayLayers = combinedLayer ? [combinedLayer] : [];
      } else if (this.currentAttendeeView === null) {
        displayLayers = attendeeLayers;
      } else {
        displayLayers = attendeeLayers.filter((layer: any) => 
          layer.meta?.attendeeEmail === this.currentAttendeeView
        );
      }

      this.timelineGrid.innerHTML = "";

      const header = document.createElement("div");
      header.className = "timeline-header";

      const timeCol = document.createElement("div");
      timeCol.className = "timeline-row-label";
      // Display timezone abbreviation in the Time column header
      const tzAbbr = this.getTimezoneAbbreviation(timezone);
      timeCol.textContent = `Time (${tzAbbr})`;
      header.appendChild(timeCol);

      displayLayers.forEach((layer: any) => {
        const col = document.createElement("div");
        col.className = "timeline-col-header";
        col.textContent = (layer.meta?.attendeeName || layer.name || "Attendee") as string;
        header.appendChild(col);
      });

      this.timelineGrid.appendChild(header);

      // Calculate recommendation slot range
      let recStartSlot: number | undefined;
      let recEndSlot: number | undefined;

      if (this.currentResult?.rec) {
        try {
          const [recStart, recEnd] = timeWindowToDates(this.currentResult.rec);
          const dayStartMs = baseDate.getTime();
          const dayEndMs = dayStartMs + 24 * 60 * 60 * 1000;
          
          if (recStart.getTime() < dayEndMs && recEnd.getTime() > dayStartMs) {
            recStartSlot = msToSlot(recStart.getTime());
            recEndSlot = msToSlot(recEnd.getTime());
          }
        } catch (error) {
          console.warn("[Taskpane] Could not calculate recommendation slots:", error);
        }
      }

      // Create rows based on configurable scale
      const minutesPerDay = 24 * 60;
      const rowCount = minutesPerDay / this.currentTimelineScale;
      const slotsPerCell = this.currentTimelineScale / 15; // 15-minute slots

      for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
        const cellStartSlot = dayStartSlot + rowIndex * slotsPerCell;
        const cellEndSlot = cellStartSlot + slotsPerCell;

        const row = document.createElement("div");
        row.className = "timeline-row";

        const label = document.createElement("div");
        label.className = "timeline-row-label";
        label.textContent = this.formatTimeFromSlot(cellStartSlot);
        row.appendChild(label);

        displayLayers.forEach((layer: any) => {
          row.appendChild(this.renderTimelineCell(layer, cellStartSlot, cellEndSlot, recStartSlot, recEndSlot));
        });

        this.timelineGrid.appendChild(row);
      }

      console.log(
        `[Taskpane] Timeline grid populated with ${rowCount} rows for ${displayLayers.length} attendees`
      );
    } catch (error) {
      console.error("[Taskpane] Failed to render timeline:", error);
      this.timelineGrid.textContent = "Error displaying timeline. Please try again.";
    }
  }

  private renderTimelineCell(
    layer: any, 
    startSlot: number, 
    endSlot: number,
    highlightStart?: number,
    highlightEnd?: number
  ): HTMLDivElement {
    // Find segments that overlap with this hour
    const relevantSegments = (layer.segments || []).filter(
      (seg: any) => seg.start < endSlot && seg.end > startSlot
    );

    const cell = document.createElement("div");
    cell.classList.add("timeline-cell");
    cell.dataset.startSlot = startSlot.toString();
    cell.dataset.endSlot = endSlot.toString();

    if (relevantSegments.length === 0) {
      cell.classList.add("timeline-free");
      cell.textContent = "Free";
      
      // Add highlighting if overlaps with recommendation
      if (highlightStart !== undefined && highlightEnd !== undefined) {
        const hasOverlap = startSlot < highlightEnd && endSlot > highlightStart;
        if (hasOverlap) {
          cell.classList.add("timeline-recommended");
        }
      }
      
      // Add selection highlighting
      if (this.userSelectedTimeRange) {
        const hasOverlap = startSlot < this.userSelectedTimeRange.end && 
                           endSlot > this.userSelectedTimeRange.start;
        if (hasOverlap) {
          cell.classList.add("timeline-selected");
        }
      }
      
      // Add event listeners for selection
      cell.addEventListener("mousedown", (e) => 
        this.handleTimelineCellMouseDown(e, startSlot, endSlot)
      );
      cell.addEventListener("mouseenter", () => 
        this.handleTimelineCellMouseEnter(startSlot, endSlot)
      );
      cell.style.cursor = "pointer";
      
      return cell;
    }

    // Calculate min/max values across all segments for this attendee
    const allValues = layer.segments.map((s: any) => s.value);
    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);
    const range = maxValue - minValue;

    // Calculate average value for this hour
    let totalValue = 0;
    let totalSlots = 0;

    relevantSegments.forEach((seg: any) => {
      const overlapStart = Math.max(seg.start, startSlot);
      const overlapEnd = Math.min(seg.end, endSlot);
      const overlapSlots = overlapEnd - overlapStart;
      totalValue += seg.value * overlapSlots;
      totalSlots += overlapSlots;
    });

    if (totalSlots === 0) {
      cell.classList.add("timeline-free");
      cell.textContent = "Free";
      
      // Add highlighting if overlaps with recommendation
      if (highlightStart !== undefined && highlightEnd !== undefined) {
        const hasOverlap = startSlot < highlightEnd && endSlot > highlightStart;
        if (hasOverlap) {
          cell.classList.add("timeline-recommended");
        }
      }
      
      // Add selection highlighting
      if (this.userSelectedTimeRange) {
        const hasOverlap = startSlot < this.userSelectedTimeRange.end && 
                           endSlot > this.userSelectedTimeRange.start;
        if (hasOverlap) {
          cell.classList.add("timeline-selected");
        }
      }
      
      // Add event listeners for selection
      cell.addEventListener("mousedown", (e) => 
        this.handleTimelineCellMouseDown(e, startSlot, endSlot)
      );
      cell.addEventListener("mouseenter", () => 
        this.handleTimelineCellMouseEnter(startSlot, endSlot)
      );
      cell.style.cursor = "pointer";
      
      return cell;
    }

    const avgValue = totalValue / totalSlots;

    // Classify as easy/medium/hard using timeline-utils
    const difficulty = classifySegmentDifficulty(avgValue, minValue, maxValue);
    const labelMap = {
      easy: "Flexible",
      medium: range === 0 ? "Busy" : "Disruptive",
      hard: "Protected",
    };
    const label = labelMap[difficulty];

    cell.classList.add(`timeline-${difficulty}`);
    cell.textContent = label;
    
    // Add highlighting if overlaps with recommendation
    if (highlightStart !== undefined && highlightEnd !== undefined) {
      const hasOverlap = startSlot < highlightEnd && endSlot > highlightStart;
      if (hasOverlap) {
        cell.classList.add("timeline-recommended");
      }
    }
    
    // Add selection highlighting
    if (this.userSelectedTimeRange) {
      const hasOverlap = startSlot < this.userSelectedTimeRange.end && 
                         endSlot > this.userSelectedTimeRange.start;
      if (hasOverlap) {
        cell.classList.add("timeline-selected");
      }
    }
    
    // Add event listeners for selection
    cell.addEventListener("mousedown", (e) => 
      this.handleTimelineCellMouseDown(e, startSlot, endSlot)
    );
    cell.addEventListener("mouseenter", () => 
      this.handleTimelineCellMouseEnter(startSlot, endSlot)
    );
    cell.style.cursor = "pointer";
    
    return cell;
  }

  private formatHour(hour: number): string {
    if (hour === 0) return "12 AM";
    if (hour < 12) return `${hour} AM`;
    if (hour === 12) return "12 PM";
    return `${hour - 12} PM`;
  }

  private getTimezoneAbbreviation(timezone: string): string {
    try {
      // Get timezone abbreviation using Intl API
      const date = new Date();
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'short'
      }).formatToParts(date);
      
      const tzPart = parts.find(part => part.type === 'timeZoneName');
      return tzPart?.value || timezone;
    } catch (error) {
      // If timezone is invalid, return the timezone string itself
      return timezone;
    }
  }

  private formatTimeFromSlot(slot: number): string {
    const ms = slot * 15 * 60 * 1000;
    const date = new Date(ms);
    const timezone = this.context?.timezone || "UTC";
    
    // Format using the context timezone
    const hours = parseInt(date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      hour12: false,
      timeZone: timezone
    }));
    const minutes = parseInt(date.toLocaleTimeString('en-US', {
      minute: '2-digit',
      timeZone: timezone
    }));
    
    // Format as HH:MM AM/PM
    const hour12 = hours % 12 || 12;
    const ampm = hours < 12 ? 'AM' : 'PM';
    const minuteStr = minutes.toString().padStart(2, '0');
    
    return `${hour12}:${minuteStr} ${ampm}`;
  }

  private handleTimelineScaleChange(newScale: number): void {
    this.currentTimelineScale = newScale;
    this.renderTimeline();
  }

  private handleTimelineCellMouseDown(event: MouseEvent, cellStartSlot: number, cellEndSlot: number): void {
    event.preventDefault();
    event.stopPropagation();
    this.isSelecting = true;
    this.selectionStartSlot = cellStartSlot;
    this.userSelectedTimeRange = { start: cellStartSlot, end: cellEndSlot };
    this.updateSelectionHighlight();
  }

  private handleTimelineCellMouseEnter(cellStartSlot: number, cellEndSlot: number): void {
    if (!this.isSelecting || this.selectionStartSlot === null) return;
    const start = Math.min(this.selectionStartSlot, cellStartSlot);
    const end = Math.max(this.selectionStartSlot, cellEndSlot);
    this.userSelectedTimeRange = { start, end };
    this.updateSelectionHighlight();
  }

  private handleTimelineMouseUp(): void {
    if (this.isSelecting) {
      this.isSelecting = false;
      this.selectionStartSlot = null;
      if (this.userSelectedTimeRange) {
        this.updateBookingButton();
      }
    }
  }

  private updateSelectionHighlight(): void {
    // Update selection highlighting without full re-render
    const cells = this.timelineGrid.querySelectorAll('.timeline-cell');
    cells.forEach((cell) => {
      const cellElement = cell as HTMLElement;
      // Remove existing selection class
      cellElement.classList.remove('timeline-selected');
    });

    // Add selection class to cells in range
    if (this.userSelectedTimeRange) {
      cells.forEach((cell) => {
        const cellElement = cell as HTMLElement;
        const startSlot = parseInt(cellElement.dataset.startSlot || '0');
        const endSlot = parseInt(cellElement.dataset.endSlot || '0');
        
        if (startSlot > 0 && endSlot > 0) {
          const hasOverlap = startSlot < this.userSelectedTimeRange!.end && 
                             endSlot > this.userSelectedTimeRange!.start;
          if (hasOverlap) {
            cellElement.classList.add('timeline-selected');
          }
        }
      });
    }
  }

  private clearTimeSelection(): void {
    this.userSelectedTimeRange = null;
    this.renderTimeline();
    this.updateBookingButton();
  }

  private updateBookingButton(): void {
    const clearBtn = document.getElementById("clear-time-selection");
    
    if (this.userSelectedTimeRange) {
      const startMs = slotToMs(this.userSelectedTimeRange.start);
      const endMs = slotToMs(this.userSelectedTimeRange.end);
      const startDate = new Date(startMs);
      const endDate = new Date(endMs);
      
      const timezone = this.context?.timezone || "UTC";
      const startStr = startDate.toLocaleTimeString(undefined, { 
        hour: "numeric", 
        minute: "2-digit",
        timeZone: timezone
      });
      const endStr = endDate.toLocaleTimeString(undefined, { 
        hour: "numeric", 
        minute: "2-digit",
        timeZone: timezone
      });
      
      this.bookMeetingBtn.textContent = `Schedule: ${startStr} – ${endStr}`;
      if (clearBtn) clearBtn.style.display = "inline-block";
    } else {
      this.bookMeetingBtn.textContent = "Book this meeting";
      if (clearBtn) clearBtn.style.display = "none";
    }
  }

  private handleAttendeeViewChange(value: string): void {
    if (value === "all") {
      this.currentAttendeeView = null;
    } else if (value === "combined") {
      this.currentAttendeeView = "combined";
    } else {
      this.currentAttendeeView = value;
    }
    this.renderTimeline();
  }

  private createCombinedLayer(): any {
    const attendeeLayers = this.timelineLayers.filter(
      (layer: any) => layer.meta?.kind === "attendee"
    );
    
    if (attendeeLayers.length === 0) return null;
    
    // Collect all slots with MAX value (hardest to schedule)
    const slotMap = new Map<number, number[]>();
    
    attendeeLayers.forEach((layer: any) => {
      if (!layer.segments) return;
      layer.segments.forEach((seg: any) => {
        for (let slot = seg.start; slot < seg.end; slot++) {
          if (!slotMap.has(slot)) {
            slotMap.set(slot, []);
          }
          slotMap.get(slot)!.push(seg.value);
        }
      });
    });
    
    // Convert to RLE segments
    const combinedSegments: any[] = [];
    const sortedSlots = Array.from(slotMap.keys()).sort((a, b) => a - b);
    
    if (sortedSlots.length === 0) return null;
    
    let currentSegment = {
      start: sortedSlots[0],
      end: sortedSlots[0] + 1,
      value: Math.max(...slotMap.get(sortedSlots[0])!)
    };
    
    for (let i = 1; i < sortedSlots.length; i++) {
      const slot = sortedSlots[i];
      const maxValue = Math.max(...slotMap.get(slot)!);
      
      if (slot === currentSegment.end && maxValue === currentSegment.value) {
        currentSegment.end = slot + 1;
      } else {
        combinedSegments.push(currentSegment);
        currentSegment = { start: slot, end: slot + 1, value: maxValue };
      }
    }
    
    combinedSegments.push(currentSegment);
    
    return {
      id: "combined",
      name: "Team Availability",
      segments: combinedSegments,
      meta: { kind: "combined" }
    };
  }

  private populateAttendeeViewSelector(): void {
    const selector = document.getElementById("attendee-view-select") as HTMLSelectElement;
    if (!selector) return;
    
    selector.innerHTML = `
      <option value="all">All Attendees</option>
      <option value="combined">Combined View</option>
    `;
    
    const attendeeLayers = this.timelineLayers.filter(
      (layer: any) => layer.meta?.kind === "attendee"
    );
    
    attendeeLayers.forEach((layer: any) => {
      const email = layer.meta?.attendeeEmail;
      const name = layer.meta?.attendeeName || email;
      if (email) {
        const option = document.createElement("option");
        option.value = email;
        option.textContent = name;
        selector.appendChild(option);
      }
    });
    
    selector.value = "all";
    this.currentAttendeeView = null;
  }

  private showAttendeeError(message: string): void {
    if (!this.attendeeError) return;
    this.attendeeError.textContent = message;
    this.attendeeError.classList.toggle("hidden", !message);
  }

  private normalizeResultPayload(statusRow: TimeFinderRequestStatusDto | null): {
    result: TimeFinderResult | null;
    diagnostics: unknown;
  } {
    if (!statusRow) {
      return { result: null, diagnostics: null };
    }

    let payload: any = statusRow.data ?? null;
    if (typeof payload === "string") {
      try {
        payload = JSON.parse(payload);
      } catch {
        // leave payload as-is if parsing fails
      }
    }

    const container = payload?.result ?? payload ?? null;
    const rec = container?.rec ?? container?.recommended ?? container?.recommendation ?? null;
    const alts = Array.isArray(container?.alts)
      ? container.alts
      : Array.isArray(container?.alternatives)
        ? container.alternatives
        : [];
    const attendeeLayers =
      container?.attendeeLayers ??
      container?.layers ??
      payload?.attendeeLayers ??
      payload?.layers ??
      null;

    const warnings = container?.warnings ?? payload?.warnings;
    const diagnostics = payload?.diagnostics ?? container?.diagnostics ?? null;

    if (!rec) {
      return { result: null, diagnostics };
    }

    const normalized: TimeFinderResult = {
      rec,
      alts,
      attendeeLayers: Array.isArray(attendeeLayers) ? attendeeLayers : undefined,
      warnings: warnings ?? undefined,
    };

    return { result: normalized, diagnostics };
  }

  private applySectionState(stateKey: keyof UIState, contentId: string, force?: boolean): void {
    if (typeof force === "boolean") {
      this.uiState[stateKey] = force;
    }

    const content = document.getElementById(contentId);
    const expanded = this.uiState[stateKey];
    if (content) {
      content.classList.toggle("hidden", !expanded);
    }

    const header = content?.previousElementSibling;
    const icon = header?.querySelector(".toggle-icon");
    if (icon) {
      icon.textContent = expanded ? "▲" : "▼";
    }
  }

  private getMailboxTimezone(): string {
    if (this.mockMode) {
      console.log("[Taskpane] Using mock timezone UTC");
      return "UTC";
    }
    try {
      const windowsTz = Office.context.mailbox.userProfile?.timeZone?.trim();
      if (!windowsTz) {
        console.warn("[Taskpane] Mailbox timezone missing, defaulting to UTC");
        return "UTC";
      }

      const iana = findIana(windowsTz);

      if (Array.isArray(iana) && iana.length > 0) {
        console.log(`[Taskpane] Mapped Windows TZ ${windowsTz} to IANA ${iana[0]}`);
        return iana[0];
      }

      if (typeof iana === "string" && iana.length > 0) {
        console.log(`[Taskpane] Mapped Windows TZ ${windowsTz} to IANA ${iana}`);
        return iana;
      }
      console.warn(`[Taskpane] Unable to map Windows TZ ${windowsTz}, defaulting to UTC`);

      return "UTC";
    } catch (error) {
      console.warn("[Taskpane] Error reading mailbox timezone, defaulting to UTC", error);
      return "UTC";
    }
  }

  private pickAttendeeLayers(result: TimeFinderResult | null, diagnostics: unknown): any[] {
    if (result?.attendeeLayers && result.attendeeLayers.length > 0) {
      return result.attendeeLayers as any[];
    }

    const diagLayers = (diagnostics as any)?.attendeeLayers;
    if (Array.isArray(diagLayers) && diagLayers.length > 0) {
      return diagLayers;
    }

    return [];
  }

  // Exposed for bootstrap logic outside the class
  public isMockMode(): boolean {
    return this.mockMode;
  }
}

const app = new TaskpaneApp();
let started = false;

const startApp = async (): Promise<void> => {
  if (started) return;
  started = true;
  await app.init();
};

if (typeof Office !== "undefined" && typeof Office.onReady === "function") {
  Office.onReady((info) => {
    if (info.host === Office.HostType.Outlook) {
      void startApp();
    } else if (app.isMockMode()) {
      // Permit mock mode to run in a regular browser
      console.warn(`[Taskpane] Host ${info.host} not Outlook; running in mock mode`);
      void startApp();
    }
  });
} else {
  // Browser fallback for mock/local testing when Office.js is absent
  void startApp();
}
